import { useTeam } from "../Components/Custom/Team/Store/Team";
import utilities from "../Helpers/utilities";
import { useAppState } from "../Stores/AppState";
import { useConversation } from "../Stores/Conversation";
import { useInvitation } from "../Stores/Invitation";
import { useWorkspaceTree } from "../Stores/WorkspaceTree";
import { useUserStore } from "../Stores/User";

export const REVOKED_INVITE_STATUS = 4;
export const DELETED_INVITE_STATUS = 5;

export function isDeletedInviteProfile(profile) {
    return Number(profile?.invite_status) === DELETED_INVITE_STATUS;
}

export function isVisibleDirectoryInviteProfile(profile) {
    return profile?.is_organization_user === false && ! isDeletedInviteProfile(profile);
}

export function isRevokedInviteProfile(profile) {
    return Number(profile?.invite_status) === REVOKED_INVITE_STATUS;
}

export function isRevokedInactiveInviteProfile(profile) {
    return (
        isRevokedInviteProfile(profile) &&
        (profile?.is_active === false || profile?.is_active === 0)
    );
}

export function isReinvitableOrganizationInviteProfile(profile) {
    if (profile?.type !== "business" || profile?.is_agent) {
        return false;
    }

    const status = Number(profile?.invite_status);

    return (
        status === REVOKED_INVITE_STATUS || status === DELETED_INVITE_STATUS
    );
}

export function isInactiveProfile(profile) {
    if (profile?.non_registered) {
        return false;
    }

    if (!profile || !Object.hasOwn(profile, "is_active")) {
        return false;
    }

    return profile.is_active === false || profile.is_active === 0;
}

export function isWorkpanelMembershipConversation(conversation) {
    if (!conversation) {
        return false;
    }

    return (
        conversation.is_space_group ||
        conversation.is_space ||
        conversation.is_board_group ||
        conversation.is_board ||
        conversation.is_item_group ||
        conversation.is_item ||
        conversation.is_group ||
        conversation.is_topic ||
        conversation.is_group_one_off
    );
}

export function shouldExcludeProfilesFromMembershipSearch({
    conversation = null,
    is_directory_panel_opened = false,
    is_manage_organization_role_opened = false,
    is_invites_tab = false,
} = {}) {
    if (
        is_directory_panel_opened ||
        is_manage_organization_role_opened ||
        is_invites_tab
    ) {
        return false;
    }

    return isWorkpanelMembershipConversation(conversation);
}

export function shouldExcludeRevokedInvitesFromSearch(params = {}) {
    return shouldExcludeProfilesFromMembershipSearch(params);
}

export function isProfileParticipantInConversation(conversation, profileId) {
    if (!conversation) {
        return false;
    }

    const participants = conversation.is_archived
        ? conversation.getParticipants?.()
        : conversation.getActiveParticipats?.();

    if (!participants?.length) {
        return false;
    }

    const isFake = conversation.isFake?.();
    return isFake
        ? participants.findIndex((participant) => participant == profileId) >= 0
        : participants.findIndex(
              (participant) => participant?.id == profileId,
          ) >= 0;
}

export function getLoadedConversationsForMembershipCheck(conversationStore) {
    const handlers = conversationStore?.handlers ?? [];
    const groupConversations = conversationStore?.group_conversations ?? [];
    const seenConversationIds = new Set();

    return [...handlers, ...groupConversations].filter((conversation) => {
        const conversationId = conversation?.id;

        if (conversationId == null) {
            return true;
        }

        if (seenConversationIds.has(conversationId)) {
            return false;
        }

        seenConversationIds.add(conversationId);

        return true;
    });
}

export function isExistingConversationParticipantByProfileId(user_profile_id) {
    const conversationStore = useConversation();
    const invitationStore = useInvitation();
    const profileId = Number(user_profile_id);

    if (Number.isNaN(profileId)) {
        return false;
    }

    for (const loadedConversation of getLoadedConversationsForMembershipCheck(
        conversationStore,
    )) {
        if (isProfileParticipantInConversation(loadedConversation, profileId)) {
            return true;
        }
    }

    return invitationStore.isUserParticipant(profileId);
}

export function useTeamMembers() {
    const workspaceTreeStore = useWorkspaceTree();
    const conversationStore = useConversation();
    const appStateStore = useAppState();
    const appTeam = useTeam();
    const { toggleChatPopover } = utilities();
    const userStore = useUserStore();

    function getOwnerIdForActiveConversationSpace() {
        const spaceRef = conversationStore.active?.space_id;
        if (spaceRef == null) {
            return null;
        }
        const spaceId = typeof spaceRef === "object" ? spaceRef.id : spaceRef;
        return workspaceTreeStore.getWorkspaceById(spaceId)?.owner_id ?? null;
    }

    function isUserData(user) {
        const convOwnerId = getOwnerIdForActiveConversationSpace();

        return (
            userStore.getCurrentUserProfileId() == user.id ||
            (convOwnerId == user.id &&
                !appStateStore.invite_drawer?.is_team_opened)
        );
    }

    function toggled_create_group(members) {
        if (!appStateStore.invite_drawer.is_team_opened) return;

        let logined_user = members.find((item) => isUserData(item));

        if (logined_user && !logined_user?.checked) {
            logined_user.checked = true;
            logined_user.checkedOrder = 1;
        }

        const filtered_checked = members.filter((item) => item.checked);
        const has_item_checked = Boolean(
            filtered_checked.length > (logined_user ? 1 : 0),
        );

        if (!has_item_checked && logined_user) {
            logined_user.checked = false;
            logined_user.checkedOrder = null;
            appStateStore.setLockedSelectedVariantTab(null);
        }

        appStateStore.invite_drawer.new_group = has_item_checked;

        return has_item_checked;
    }

    function searchMember(member, searchKey) {
        if (
            shouldExcludeProfilesFromMembershipSearch({
                conversation: conversationStore.active,
                is_directory_panel_opened:
                    appStateStore.invite_drawer?.is_team_opened ||
                    appStateStore.invite_drawer?.is_new_directory_opened,
                is_manage_organization_role_opened:
                    appStateStore.is_manage_organization_role_opened,
                is_invites_tab: appStateStore.tab === "invites",
            }) &&
            (isRevokedInviteProfile(member) || isInactiveProfile(member))
        ) {
            return false;
        }

        let finded = false;
        [member?.email, member?.full_name].forEach((el) => {
            finded =
                finded || el?.toLowerCase()?.includes(searchKey?.toLowerCase());
        });
        return finded;
    }

    function searchGroup(group, searchKey) {
        let finded = false;
        [group?.name].forEach((el) => {
            finded =
                finded || el?.toLowerCase()?.includes(searchKey?.toLowerCase());
        });
        return finded;
    }

    function onMemberClicked(event, member) {
        const { data, new_group } = appStateStore.invite_drawer;

        const referenced_invited = data?.name;
        const notReferencedGrouped = !new_group || !referenced_invited;
        const convOwnerId = getOwnerIdForActiveConversationSpace();

        if (
            referenced_invited ||
            !notReferencedGrouped ||
            new_group ||
            userStore.getCurrentUserProfileId() == member.id ||
            convOwnerId == member.id ||
            (event.target.tagName === "INPUT" &&
                event.target.type === "checkbox") ||
            (isUserData(member) &&
                (event.target.querySelector(
                        ".prospus-activity-badge__initial_name",
                    ) ||
                    event.target.classList.contains(
                        "prospus-activity-badge__initial_name",
                    ) ||
                    event.target.querySelector(
                        ".prospus-activity-badge__img",
                    ) ||
                    event.target.classList.contains(
                        "prospus-activity-badge__img",
                    )))
        ) {
            return;
        }
        clearAllSelected();

        if (member.is_dm_creation_in_progress) return;
        member.is_dm_creation_in_progress = true;

        conversationStore.createDMWithProfile(member.id).then((data) => {
            member.is_dm_creation_in_progress = false;
        });

        if (appStateStore.invite_drawer?.is_new_directory_opened) {
            appStateStore.toggleNewDirectoryPanel(false);
            toggleChatPopover(true, true, true);
            return;
        }

        conversationStore.toggleTeamPanel(false);
        toggleChatPopover(true);
    }

    function clearAllSelected() {
        appTeam.get_checked_invitees.forEach(
            (member) => (member.checked = false),
        );
    }

    return {
        isUserData,
        toggled_create_group,
        searchMember,
        searchGroup,
        onMemberClicked,
        clearAllSelected,
    };
}
